import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  LockOutlined,
  DownOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  UnlockOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Space, Button, Input, Select, Dropdown, Spin, InputRef } from "antd";
import { DatePicker } from "../../../components/antOverrides";
import { CSVLink } from "react-csv";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  lockMonthlySalary,
  unlockMonthlySalary,
  runMonthlySalary,
  runMonthlySalarySimulation,
  approveAllMonthlySalary,
  loadPage,
  exportSalary,
  fixPlans,
} from "../salarySlice";
import { generateSalaryFile, listEmployeesSalary } from "../../../api/server";
import {
  getExcelData,
  getSalaryExcelheaders,
  getSalaryData,
  exportSalaryToPdf,
} from "../../../components/util/helpers";
import moment, { Moment } from "moment";
import { useIntl } from "react-intl";
import { usePermission } from "../../../hooks/accessControl";
import { isAllowedFeature } from "../../organization/organizationSlice";
import { HeaderProps, RootState, SalaryParams, ExcelHeader } from "../types";

const { Option } = Select;

type RouteParams = {
  branchId: string;
};

type MenuItem = {
  key: string;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
};

/**
 * Header component for the Salary module
 *
 * This component provides the main controls for managing salary data including:
 * - Date picker for selecting month and year
 * - Branch selector (for admins)
 * - Search input for filtering employees
 * - Actions menu for operations like running salary calculations, approving salaries,
 *   exporting data, and locking/unlocking the salary period
 *
 * The component handles various permission levels and shows/hides actions accordingly.
 * It also manages asynchronous operations like file downloads and data exports.
 *
 * @param props - Component props
 * @param props.onDateChange - Callback function when date selection changes
 * @param props.activeDate - Currently selected date
 * @param props.onTextChange - Callback function when search text changes
 * @param props.onBranchChange - Callback function when branch selection changes
 * @param props.activeMonth - Currently selected month
 * @param props.activeYear - Currently selected year
 * @param props.activeBranch - Currently selected branch ID
 * @param props.textFilter - Current search text
 * @param props.summary - Salary summary data
 * @param props.salary_export_type_id - ID for salary export format
 * @param props.organizationSalarySymbols - List of salary symbols used in the organization
 * @param props.salary_column_settings - Column configuration for salary tables
 * @param props.onShowReport - Callback function to show salary report
 * @returns React component
 */
const Header: React.FC<HeaderProps> = ({
  onDateChange,
  activeDate,
  onTextChange,
  onBranchChange,
  activeMonth,
  activeYear,
  activeBranch,
  textFilter,
  summary,
  salary_export_type_id,
  organizationSalarySymbols,
  salary_column_settings,
  onShowReport,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { branchId } = useParams<RouteParams>();
  const location = useLocation();
  const inputRef = useRef<InputRef>(null);
  const csvLinkRef = useRef<any>(null);
  const [excelData, setExcelData] = useState<Record<string, any>[]>([]);
  const [excelHeaders, setExcelHeaders] = useState<ExcelHeader[] | null>(null);
  // Selectors
  const { profile, salary, customSort, scopePrefix, isLoading } = useSelector(
    (state: RootState) => ({
      profile: state.user.profile,
      salary: state.salary,
      customSort: state.salary.customSort,
      scopePrefix: state.user.scopePrefix,
      isLoading: Object.values(state.pendingActions).some(Boolean),
    })
  );

  const branch_id = activeBranch || (branchId ? parseInt(branchId) : undefined);
  const isAdmin = location.pathname.includes("admin");
  const currentDate = moment()
    .set({
      year: +(activeYear || salary.activeYear),
      month: +(activeMonth || salary.activeMonth) - 1,
    })
    .startOf("month");

  // Permissions
  const permissions = {
    export: usePermission(branchId, "branch_salary", "export"),
    run: usePermission(branchId, "branch_salary", "run"),
    approveAll: usePermission(branchId, "branch_salary", "approve_all"),
    lock: usePermission(branchId, "branch_salary", "lock"),
    unlock: usePermission(branchId, "branch_salary", "unlock"),
  };

  /**
   * Generates common params object for API calls
   * @param additionalParams - Optional additional parameters to include
   * @returns Parameters object for salary API calls
   */
  const getParams = useCallback(
    (additionalParams = {}): SalaryParams => ({
      period: {
        year: +salary.activeYear,
        month: +salary.activeMonth,
        ...additionalParams,
      },
      branchId: branch_id as number,
    }),
    [salary.activeYear, salary.activeMonth, branch_id]
  );

  // Page config
  const pageConfig = {
    ...salary.page,
    pageSize: 10000,
    scopePrefix,
    path: isAdmin ? `branches/${activeBranch}/salary` : "salary",
    query: {
      year: +(activeYear || salary.activeYear),
      month: +(activeMonth || salary.activeMonth),
      sort:
        customSort && "field" in customSort
          ? customSort.field.split("custom_sort")[1]
          : undefined,
      asc: customSort && customSort.order === "ascend",
    },
  };

  // Effects
  useEffect(() => {
    inputRef.current?.input?.focus();
  }, [branchId]);

  useEffect(() => {
    if (excelData.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
      setExcelData([]);
      setExcelHeaders([]);
    }
  }, [excelData]);

  /**
   * Opens a URL for downloading a file
   * @param url - URL to open for download
   */
  const openURL = useCallback((url: string) => {
    const downloadLink = document.createElement("a");
    Object.assign(downloadLink, {
      href: url,
      download: "salary_file",
      title: "Download",
    });
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }, []);

  /**
   * Generic handler for salary-related actions
   * @param action - Redux action to dispatch
   * @param params - Parameters for the action
   * @param reload - Whether to reload the page after the action
   * @returns Result of the dispatched action
   */
  const handleSalaryAction = useCallback(
    async (action: any, params: SalaryParams, reload = true) => {
      const result = await dispatch(action(params));
      if (!result.error && reload) {
        dispatch(loadPage(isAdmin));
      }
      return result;
    },
    [dispatch, isAdmin]
  );

  /**
   * Exports salary data to Excel format
   */
  const exportToExcel = useCallback(async () => {
    const result = await listEmployeesSalary(pageConfig);
    const data = result.data.items;
    const salaryData = getSalaryData(data, organizationSalarySymbols, intl);
    const headers =
      data.length > 0
        ? getSalaryExcelheaders(
            salaryData,
            intl,
            organizationSalarySymbols,
            salary_column_settings
          )
        : [];
    setExcelHeaders(headers);
    setExcelData(getExcelData(salaryData, headers));
  }, [pageConfig, organizationSalarySymbols, intl, salary_column_settings]);

  /**
   * Exports salary data to PDF format
   */
  const exportToPdf = useCallback(async () => {
    const result = await listEmployeesSalary(pageConfig);
    const data = result.data.items;
    const salaryData = getSalaryData(data, organizationSalarySymbols, intl);
    const headers =
      data.length > 0
        ? getSalaryExcelheaders(
            salaryData,
            intl,
            organizationSalarySymbols,
            salary_column_settings
          )
        : [];
    exportSalaryToPdf(
      salaryData,
      headers,
      { name: "" },
      { styles: { fontSize: 9 }, margin: { top: 26 } },
      "l"
    );
  }, [pageConfig, organizationSalarySymbols, intl, salary_column_settings]);

  // Action handlers
  const actions = {
    /**
     * Runs salary calculation, with optional simulation mode
     * @param simulation - Whether to run in simulation mode
     */
    runSalary: (simulation = false) =>
      handleSalaryAction(
        simulation ? runMonthlySalarySimulation : runMonthlySalary,
        getParams(simulation ? { simulation: true } : {})
      ),

    /**
     * Approves all salaries for the current period
     */
    approveAll: () => handleSalaryAction(approveAllMonthlySalary, getParams()),

    /**
     * Downloads salary file in the configured export format
     */
    downloadFile: () =>
      generateSalaryFile(
        getParams({ export_type: salary_export_type_id })
      ).then((action) => openURL(action.file_url)),

    /**
     * Exports salary data in the configured format
     */
    exportFile: () =>
      handleSalaryAction(
        exportSalary,
        getParams({ export_type: salary_export_type_id })
      ).then((action) => openURL(action.payload.file_url)),

    /**
     * Downloads salary data as Excel file
     */
    downloadExcel: () =>
      generateSalaryFile(getParams()).then((action) =>
        openURL(action.file_url)
      ),

    /**
     * Locks or unlocks the salary period
     * @param lock - Whether to lock (true) or unlock (false)
     */
    lockSalary: (lock = true) =>
      handleSalaryAction(
        lock ? lockMonthlySalary : unlockMonthlySalary,
        getParams()
      ),

    /**
     * Fixes order plans in the system
     */
    fixOrderPlans: () => dispatch(fixPlans(getParams())),
  };

  /**
   * Generates menu items based on permissions and current state
   * @returns Array of menu items
   */
  const getMenuItems = useCallback((): MenuItem[] => {
    const canGenerate =
      branch_id &&
      summary?.can_generate &&
      moment().endOf("month") > currentDate;
    const items: MenuItem[] = [];

    if (!summary?.locked && canGenerate) {
      items.push({
        key: "1",
        icon: <UnlockOutlined />,
        label: intl.formatMessage({ id: "app.salary.run_month_salary" }),
        disabled: !permissions.run,
      });
    }

    if (isAllowedFeature("salary_simulation")) {
      items.push({
        key: "11",
        icon: <UnlockOutlined />,
        label: intl.formatMessage({
          id: "app.salary.run_month_salary_simulation",
        }),
        disabled: !permissions.run,
      });
    }

    if (summary && !summary?.locked && summary.salary_run_at && canGenerate) {
      items.push({
        key: "2",
        icon: <UnlockOutlined />,
        label: intl.formatMessage({ id: "app.salary.approve_all" }),
        disabled: !permissions.approveAll,
      });
    }

    if (salary_export_type_id && branch_id) {
      items.push(
        {
          key: "3",
          icon: <DownloadOutlined />,
          label: intl.formatMessage({ id: "app.salary.download_file" }),
        },
        {
          key: "9",
          icon: <DownloadOutlined />,
          label: intl.formatMessage({ id: "app.salary.export_file" }),
          disabled: !permissions.export || !salary_export_type_id,
        }
      );
    }

    if (branch_id) {
      items.push(
        {
          key: "4",
          icon: <FileExcelOutlined />,
          label: intl.formatMessage({ id: "app.salary.download_excel" }),
        },
        {
          key: "10",
          icon: <FileExcelOutlined />,
          label: intl.formatMessage({ id: "app.salary.show_salarry_report" }),
        }
      );
    }

    if (branch_id && moment().endOf("month") > currentDate) {
      items.push({
        key: "5",
        icon: <FileExcelOutlined />,
        label: intl.formatMessage({
          id: "app.salary.download_excel_for_salary_file",
        }),
      });
    }

    if (summary?.can_lock && !summary.locked) {
      items.push({
        key: "7",
        icon: <LockOutlined />,
        label: intl.formatMessage({ id: "app.salary.lock_salary" }),
        disabled: !permissions.lock,
      });
    }

    if (summary?.locked && summary.can_generate) {
      items.push({
        key: "8",
        icon: <UnlockOutlined />,
        label: intl.formatMessage({ id: "app.salary.unlock_salary" }),
        disabled: !permissions.unlock,
      });
    }

    return items;
  }, [
    summary,
    branch_id,
    currentDate,
    salary_export_type_id,
    permissions,
    intl,
  ]);

  /**
   * Handles menu item clicks
   * @param param0 - Object containing the selected key
   */
  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const actionMap: Record<string, () => void> = {
        "1": () => actions.runSalary(false),
        "11": () => actions.runSalary(true),
        "2": actions.approveAll,
        "3": actions.downloadFile,
        "4": exportToExcel,
        "5": actions.downloadExcel,
        "6": exportToPdf,
        "7": () => actions.lockSalary(true),
        "8": () => actions.lockSalary(false),
        "9": actions.exportFile,
        ...(onShowReport ? { "10": onShowReport } : {}),
      };
      actionMap[key]?.();
    },
    [actions, exportToExcel, exportToPdf, onShowReport]
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Space>
        <DatePicker
          value={activeDate}
          onChange={onDateChange}
          picker="month"
          format="MM/YYYY"
          placeholder={intl.formatMessage({ id: "app.gaps.select_month" })}
        />
        {isAdmin && (
          <Select
            style={{ width: 200 }}
            value={activeBranch ? String(activeBranch) : undefined}
            showSearch
            placeholder={intl.formatMessage({ id: "app.users.chooseBranch" })}
            optionFilterProp="children"
            onChange={onBranchChange}
            filterOption={(input, option) =>
              (option?.label?.toString() ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={profile.branches?.map((branch) => ({
              value: String(branch.id),
              label: branch.name,
            }))}
          />
        )}
        <Input
          prefix={<SearchOutlined style={{ padding: "0 5px", opacity: 0.4 }} />}
          placeholder={intl.formatMessage({ id: "app.salary.select_employee" })}
          value={textFilter || ""}
          onChange={onTextChange}
          style={{ width: 300 }}
          allowClear
          ref={inputRef}
        />
      </Space>

      {isAdmin && !branch_id && activeYear && activeMonth && (
        <Button onClick={actions.fixOrderPlans} loading={isLoading}>
          {intl.formatMessage({ id: "app.salary.fix_order_plans" })}
        </Button>
      )}

      {branch_id && (
        <Space>
          {isLoading && <Spin size="small" />}
          <div style={{ display: "none" }}>
            <CSVLink
              data={excelData}
              headers={excelHeaders || []}
              asyncOnClick
              filename={`salary_${salary.activeMonth}_${salary.activeYear}.csv`}
              ref={csvLinkRef}
            />
          </div>
          <Dropdown
            disabled={isLoading}
            menu={{ items: getMenuItems(), onClick: handleMenuClick }}
            trigger={["click"]}
            placement="bottomLeft"
          >
            <Button type="link">
              {intl.formatMessage({ id: "app.salary.options" })}
              <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      )}
    </div>
  );
};

export default Header;
